import React, { useState } from "react";
import {Box, Typography, Card, Tabs, Tab, Container, styled, Divider, Stack, Button, Grid} from "@mui/material";
import { FaComments, FaCheckCircle } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

// --- Styled Components (The "Const" Style Toolkit) ---

const DetailWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#FDFDFD",
    padding: theme.spacing(8, 0),
}));

const CourseCard = styled(Card)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    minHeight: "500px",
    borderRadius: "24px",
    border: "1px solid #eee",
    boxShadow: "0 20px 50px rgba(0,0,0,0.05)",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
        flexDirection: "column",
    },
}));

const NavigationPane = styled(Box)(({ theme }) => ({
    width: "300px",
    backgroundColor: "#09122C",
    padding: theme.spacing(4, 2),
    [theme.breakpoints.down("md")]: {
        width: "100%",
        padding: theme.spacing(2),
    },
}));

const ContentPane = styled(Box)(({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(6),
    backgroundColor: "#FFFFFF",
    [theme.breakpoints.down("md")]: {
        padding: theme.spacing(4),
    },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    color: "rgba(255,255,255,0.6)",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.95rem",
    alignItems: "flex-start",
    textAlign: "left",
    padding: "16px 20px",
    borderRadius: "12px",
    marginBottom: "8px",
    transition: "all 0.3s ease",
    "&.Mui-selected": {
        color: "#B8860B",
        backgroundColor: "rgba(184, 134, 11, 0.1)",
    },
    "&:hover": {
        color: "#FFF",
        backgroundColor: "rgba(255,255,255,0.05)",
    },
}));

const BulletItem = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
    color: "#374151",
});

// --- Main Component ---

export default function AdvancedEnglishProfessional() {
    const [tabIndex, setTabIndex] = useState(0);

    const sections = [
        "Summary",
        "Course Objectives",
        "Skill Areas",
        "Business Relevance",
        "Suitable For",
        "Certification & Exams",
    ];

    const handleChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const renderContent = () => {
        switch (tabIndex) {
            case 0:
                return (
                    <Box>
                        <Typography variant="h4" fontWeight={900} color="#09122C" gutterBottom>Executive Summary</Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.8, color: "#4B5563", fontSize: "1.1rem" }}>
                            This Advanced English course is specifically engineered for high-level professionals
                            operating within international business ecosystems. We move beyond basic fluency to focus on
                            <strong> corporate precision</strong>, high-stakes negotiation phrasing, and the
                            nuances of executive-level functions.
                        </Typography>
                    </Box>
                );
            case 1:
                return (
                    <Box>
                        <Typography variant="h4" fontWeight={900} color="#09122C" gutterBottom>Course Objectives</Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 3 }}>
                            Learners will evolve their communication through four strategic pillars:
                        </Typography>
                        {["Mastering high-level grammar for formal documentation", "Perfecting business presentation delivery", "Persuasive speaking and strategic vocabulary", "Developing a professional tone for global corporations"].map((text, i) => (
                            <BulletItem key={i}>
                                <FaCheckCircle color="#B8860B" />
                                <Typography fontWeight={500}>{text}</Typography>
                            </BulletItem>
                        ))}
                    </Box>
                );
            case 2:
                return (
                    <Box>
                        <Typography variant="h4" fontWeight={900} color="#09122C" gutterBottom>Skill Areas</Typography>
                        <Stack spacing={2} sx={{ mt: 3 }}>
                            {["Formal email and report writing", "Public speaking and boardroom communication", "Negotiation and conflict resolution phrasing", "Cross-cultural etiquette in English"].map((skill, i) => (
                                <Box key={i} sx={{ p: 2, bgcolor: "#f8f9fa", borderRadius: "8px", borderLeft: "4px solid #B8860B" }}>
                                    <Typography fontWeight={700}>{skill}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                );
            case 5:
                return (
                    <Box>
                        <Typography variant="h4" fontWeight={900} color="#09122C" gutterBottom>Certification & Exams</Typography>
                        <Typography sx={{ mb: 4 }}>Our program is aligned with global standards to ensure your credentials carry weight.</Typography>
                        <Grid container spacing={2}>
                            {["IELTS Academic", "TOEFL iBT", "CEFR C1/C2"].map((cert) => (
                                <Grid item xs={12} sm={4} key={cert}>
                                    <Box sx={{ textAlign: "center", p: 3, border: "1px solid #eee", borderRadius: "16px" }}>
                                        <Typography fontWeight={900} color="#B8860B">{cert}</Typography>
                                        <Typography variant="caption">Readiness Supported</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                );
            default:
                return <Typography sx={{ lineHeight: 1.8 }}>Content for this section is being updated to our latest corporate standards.</Typography>;
        }
    };

    return (
        <DetailWrapper>
            <Container maxWidth="lg">
                <Box sx={{ mb: 6, textAlign: "center" }}>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, mb: 2 }}>
                        <FaComments size={32} color="#B8860B" />
                        <Typography variant="h3" fontWeight={900} color="#09122C" sx={{ letterSpacing: "-0.04em" }}>
                            Advanced Business English
                        </Typography>
                    </Box>
                    <Typography variant="h6" color="text.secondary" fontWeight={400}>
                        Corporate & Executive Communication Mastery
                    </Typography>
                </Box>

                <CourseCard>
                    <NavigationPane>
                        <Tabs
                            orientation="vertical"
                            value={tabIndex}
                            onChange={handleChange}
                            sx={{
                                "& .MuiTabs-indicator": { display: "none" },
                            }}
                        >
                            {sections.map((label, index) => (
                                <StyledTab key={index} label={label} />
                            ))}
                        </Tabs>

                        <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", my: 4 }} />

                        <Box sx={{ px: 2, textAlign: "center" }}>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mb: 2 }}>
                                READY TO BEGIN?
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                sx={{ bgcolor: "#B8860B", color: "#000", fontWeight: 800, textTransform: "none", borderRadius: "8px", "&:hover": { bgcolor: "#D4AF37" } }}
                            >
                                Enroll Now
                            </Button>
                        </Box>
                    </NavigationPane>

                    <ContentPane>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={tabIndex}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                {renderContent()}
                            </motion.div>
                        </AnimatePresence>
                    </ContentPane>
                </CourseCard>
            </Container>
        </DetailWrapper>
    );
}
